import map from 'lodash/map';
import uniqBy from 'lodash/uniqBy';
import orderBy from 'lodash/orderBy';
import intersection from 'lodash/intersection';
import uniq from 'lodash/uniq';
import { FILTERS, FILTERS_COLLECT, AIRPORT_FILTERS, WINTER_FILTERS } from '../constants/default';
import { parseDate } from './dateUtils';

/**
 * Checks if rental dates fall within winter period (October 15 to April 10)
 * @param {string|Date} pickupDate - The pickup date
 * @param {string|Date} dropoffDate - The dropoff date (optional, defaults to pickup date)
 * @returns {boolean} True if dates fall within winter period
 */
export function isWinterPeriod(pickupDate, dropoffDate = null) {
  if (!pickupDate) return false;
  
  const pickup = parseDate(pickupDate);
  const dropoff = dropoffDate ? parseDate(dropoffDate) : pickup;
  
  // Check if either pickup or dropoff date falls within winter period
  const dates = [pickup, dropoff];
  
  return dates.some(date => {
    const month = date.getMonth() + 1; // getMonth() is 0-based
    const day = date.getDate();
    
    // Winter period: Oct 15 - Apr 10
    // October 15 onwards (month 10, day >= 15)
    if (month === 10 && day >= 15) return true;
    // November through December (months 11-12)
    if (month === 11 || month === 12) return true;
    // January through March (months 1-3)
    if (month >= 1 && month <= 3) return true;
    // April 1-10 (month 4, day <= 10)
    if (month === 4 && day <= 10) return true;
    
    return false;
  });
}

// Utility to get matched offers for a filter value, supporting filterFunction and field paths
export function getMatchedOffersForFilterValue({ data, param, value }) {
  if (param.filterFunction) {
    return data.filter(offer => param.filterFunction(offer, value));
  }
  const arr = param?.field?.split('.') || [];
  if (arr.length === 1) {
    return data.filter(offer => offer[arr[0]] !== undefined && offer[arr[0]] === value);
  } else if (arr.length === 2) {
    return data.filter(offer => offer[arr[0]] && offer[arr[0]][arr[1]] !== undefined && offer[arr[0]][arr[1]] === value);
  }
  return [];
}

export function normalizeFilterValue(val) {
  if (Array.isArray(val)) return val;
  if (val == null) return [];
  return [val].filter(Boolean);
}

/**
 * Filters the data array based on the provided filters and isAirport flag.
 * This centralizes the filtering logic for Search.js and widgets.
 * @param {Array} data - The offers data
 * @param {Object} filters - The active filters
 * @param {boolean} isAirport - Whether airport filters should be included
 * @param {string|Date} pickupDate - The pickup date for winter filter logic
 * @param {string|Date} dropoffDate - The dropoff date for winter filter logic
 * @returns {Array} Filtered results
 */
export function filterResults(data, filters, isAirport = false, pickupDate = null, dropoffDate = null) {
  if (!Array.isArray(data)) return [];
  // Filter out items without a model (error data)
  let result = data.filter(item => item.model);

  // Merge all possible filters
  let localFilters = { ...FILTERS_COLLECT, ...FILTERS };
  if (isAirport) {
    Object.assign(localFilters, AIRPORT_FILTERS);
  }
  if (isWinterPeriod(pickupDate, dropoffDate)) {
    Object.assign(localFilters, WINTER_FILTERS);
  }

  // Apply each filter if it has a value
  Object.entries(localFilters).forEach(([key, param]) => {
    if (filters[key] && filters[key].length > 0) {
      if (param.filterFunction) {
        result = param.filterFunction(result, filters[key]);
      } else if (param.field) {
        const arr = param.field.split('.');
        if (arr.length === 1) {
          result = result.filter(item => filters[key].includes(item[arr[0]] && `${item[arr[0]]}`));
        } else if (arr.length === 2) {
          result = result.filter(item => filters[key].includes(item[arr[0]] && `${item[arr[0]][arr[1]]}`));
        }
      }
    }
  });
  return result;
}

/**
 * Builds all filters (static and dynamic), calculates counts for each filter value,
 * and handles cases where multiple filters with the same baseFilter are used.
 * @param {Object} params
 * @param {Array} params.data - The offers data
 * @param {Object} params.values - The current filter values
 * @param {boolean} params.isAirport - Whether airport filters should be included
 * @param {string} params.locale - The locale string
 * @param {string|Date} params.pickupDate - The pickup date for winter filter logic
 * @param {string|Date} params.dropoffDate - The dropoff date for winter filter logic
 * @returns {Object} allFilters with updated counts and matchedOffers
 */
export function getAllFiltersWithCounts({ data = [], values = {}, isAirport = false, locale = 'en', pickupDate = null, dropoffDate = null }) {
  let filters = { ...FILTERS_COLLECT };
  if (isAirport) {
    filters = { ...filters, ...AIRPORT_FILTERS };
  }
  // Note: WINTER_FILTERS should NOT be added to FILTERS_COLLECT since they use filterFunction, not field

  // Generate dynamic filters from data
  const collected = {};
  map(filters, (param, key) => {
    const arr = param.field?.split('.') || [];
    const mapped = map(data, item => {
      const ret = {};
      if (item[arr[0]] && typeof (item[arr[0]][arr[1]]) !== 'undefined') {
        ret.value = item[arr[0]][arr[1]];
        ret.title = item[arr[0]][arr[1]];
        if (param.currency && item[arr[0]]["currency"]) {
          ret.value = ret.value.toLocaleString(locale, { style: 'currency', currency: item[arr[0]]["currency"] });
          ret.title = ret.title.toLocaleString(locale, { style: 'currency', currency: item[arr[0]]["currency"] });
        }
      }
      return ret;
    });
    // Only keep items with non-null value and title
    const filtered = mapped.filter(item => item.value != null && item.title != null);
    const uniqs = uniqBy(filtered, 'title');
    const sorted = orderBy(uniqs, [uniq => {
      if (uniq && uniq.value) {
        return typeof (uniq.value) === 'string' ? uniq.value.toLowerCase() : uniq.value;
      } else {
        return null;
      }
    }]);
    const items = map(sorted, value => ({ value: value.value, title: value.title }));
    collected[key] = {
      title: param.title,
      field: param.field,
      items: items
    };
  });

  // Merge static and dynamic filters
  const allFilters = {};
  Object.keys(FILTERS).forEach(key => {
    allFilters[key] = { ...FILTERS[key] };
  });
  if (isWinterPeriod(pickupDate, dropoffDate)) {
    Object.keys(WINTER_FILTERS).forEach(key => {
      allFilters[key] = { ...WINTER_FILTERS[key] };
    });
  }
  Object.keys(collected).forEach(key => {
    allFilters[key] = { ...collected[key] };
  });

  // For each filter, calculate matchedOffers and initial count
  map(allFilters, (param, key) => {
    param.items = map(param.items, item => {
      const matchedOffers = getMatchedOffersForFilterValue({ data, param, value: item.value });
      item.count = matchedOffers.length;
      item.matchedOffers = matchedOffers;
      return item;
    });
    return param;
  });

  // Determine if any filter is set
  let isAnyFilterSet = false;
  map(values, (val) => {
    if (Array.isArray(val) && val.length > 0) {
      isAnyFilterSet = true;
    }
  });

  // If any filter is set, recalculate counts considering other active filters
  if (isAnyFilterSet) {
    map(allFilters, (param, key) => {
      param.currentlyFilteredOffersForGroup = [];
      param.isUsed = values && values[param.title] && values[param.title].length > 0;
      param.items.forEach(i => {
        if (!param.isUsed || (values && values[param.title] && values[param.title].indexOf(String(i.value)) > -1)) {
          param.currentlyFilteredOffersForGroup.push(...i.matchedOffers);
        }
      });
      param.currentlyFilteredOffersForGroup = uniq(param.currentlyFilteredOffersForGroup);
    });

    map(allFilters, (param, key) => {
      let allOtherMatchedOffers = [...data];
      map(allFilters, (otherParam, otherKey) => {
        // If filters share the same baseFilter, treat them as a group
        const sameBase = (param.baseFilter && otherParam.baseFilter && param.baseFilter === otherParam.baseFilter);
        if (param.title !== otherParam.title && otherParam.isUsed && !sameBase) {
          allOtherMatchedOffers = intersection(allOtherMatchedOffers, otherParam.currentlyFilteredOffersForGroup);
        }
      });
      param.items = map(param.items, item => {
        const matchedFilterOffers = intersection(item.matchedOffers, allOtherMatchedOffers);
        const finalMatchedOffers = uniq([...matchedFilterOffers]);
        item.count = finalMatchedOffers.length;
        return item;
      });
      return param;
    });
  }

  return allFilters;
}
